import type { IFuseOptions } from "fuse.js";

import { Module, ModuleOperation } from "src/api/modulesApi";
import { PillVariants } from "src/base-components/Pill";
import { ModuleKind, ModuleOperationStatus } from "src/clients/flow-api";

export const getModuleKindDisplayName = (kind: ModuleKind): string =>
  kind
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export type SearchableModule = Module & {
  _workspaceName: string;
  _workspaceSlug: string;
  _orgName: string;
  _operationText: string;
};

export const enrichModulesForSearch = (
  modules: Module[],
  workspaceMap: Map<string, { name: string; orgId: string; slug: string }>,
  orgMap: Map<string, string>,
): SearchableModule[] =>
  modules.map((mod) => {
    const ws = workspaceMap.get(mod.workspaceId);
    return {
      ...mod,
      _workspaceName: ws?.name ?? "",
      _workspaceSlug: ws?.slug ?? "",
      _orgName: ws?.orgId ? (orgMap.get(ws.orgId) ?? "") : "",
      _operationText: mod.activeOperations
        .map(
          (op) =>
            `${JSON.stringify(op.patch)}${op.tags ? ` ${JSON.stringify(op.tags)}` : ""}`,
        )
        .join(" "),
    };
  });

export const MODULE_SEARCH_OPTIONS: IFuseOptions<SearchableModule> = {
  keys: [
    "id",
    "workspaceId",
    "kind",
    "_workspaceName",
    "_workspaceSlug",
    "_orgName",
    "activeOperations.id",
    "activeOperations.status",
    "_operationText",
  ],
  threshold: 0.3,
  ignoreLocation: true,
};

export const getOperationStatusPillVariant = (
  status: ModuleOperationStatus,
): PillVariants => {
  switch (status) {
    case ModuleOperationStatus.COMPLETED:
      return "green";
    case ModuleOperationStatus.IN_PROGRESS:
    case ModuleOperationStatus.PENDING:
      return "yellow";
    case ModuleOperationStatus.FAILED:
      return "red";
    case ModuleOperationStatus.SCHEDULED:
    case ModuleOperationStatus.CANCELLED:
      return "gray";
  }
};

export const getModuleOperationStatus = (
  mod: Module,
): ModuleOperationStatus | null => {
  if (mod.latestResolvedOperation?.status === ModuleOperationStatus.FAILED) {
    return ModuleOperationStatus.FAILED;
  }
  if (mod.activeOperations.length > 0) {
    const oldest = mod.activeOperations.reduce((a, b) =>
      (a.createdAt ?? "") < (b.createdAt ?? "") ? a : b,
    );
    return oldest.status;
  }
  return mod.latestResolvedOperation?.status ?? null;
};

export const getOperationStatusLabel = (
  status: ModuleOperationStatus,
): string =>
  status
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

export const OPERATION_STATUS_FILTER_OPTIONS = Object.values(
  ModuleOperationStatus,
)
  .filter((status) => status !== ModuleOperationStatus.CANCELLED)
  .map((status) => ({
    value: status,
    label: getOperationStatusLabel(status),
  }));

export const operationMatchesSearch = (
  op: ModuleOperation,
  lower: string,
): boolean =>
  op.id.toLowerCase().includes(lower) ||
  op.status.toLowerCase().includes(lower) ||
  JSON.stringify(op.patch).toLowerCase().includes(lower) ||
  (op.tags ? JSON.stringify(op.tags).toLowerCase().includes(lower) : false);

export const moduleHasOperationMatch = (
  mod: Module,
  searchTerm: string,
): boolean => {
  if (!searchTerm) return false;
  const lower = searchTerm.toLowerCase();
  return mod.activeOperations.some((op) => operationMatchesSearch(op, lower));
};

export const truncateJson = (
  obj: Record<string, unknown>,
  maxLength = 120,
): string => {
  const str = JSON.stringify(obj);
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "…";
};
